/*
  <bundle-preview>
  Live summary of the current bundle. Reads /cart.js, filters bundle line items
  (properties._bundle_mode === 'weight_bundle') and computes weight / discount
  from the tier config on #bundle-dashboard-root (the bundle app block).
  Re-renders on cart events. Self-contained in the theme.
*/
if (!customElements.get('bundle-preview')) {
  class BundlePreview extends HTMLElement {
    connectedCallback() {
      this.$ = (sel) => this.querySelector(sel);
      this.list = this.$('[data-bpv-list]');
      this.refresh = this.refresh.bind(this);

      // React to cart changes (theme events + generic ones).
      ['on:cart:add', 'on:line-item:change', 'on:cart:error'].forEach((ev) =>
        document.addEventListener(ev, this.refresh)
      );
      document.addEventListener('cart:refresh', this.refresh);
      document.addEventListener('cart:updated', this.refresh);
      window.addEventListener('pageshow', this.refresh);

      // Remove-item clicks (delegated).
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-bpv-remove]');
        if (btn) {
          e.preventDefault();
          if (btn.classList.contains('is-loading')) return; // already removing
          // Show a spinner in place of the trash icon while the line is removed.
          btn.classList.add('is-loading');
          btn.disabled = true;
          btn.setAttribute('aria-busy', 'true');
          this.removeLine(btn.getAttribute('data-bpv-remove'));
        }
      });

      this.setupDrawer();
      this.refresh();
    }

    /* ---------- Mobile / tablet bottom drawer ---------- */
    setupDrawer() {
      const peek = this.querySelector('.bpv__peek');
      if (!peek) return;

      // Open: "View Bundle" button, or tapping the collapsed bar.
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-bpv-close]')) {
          e.preventDefault();
          this.closeDrawer();
          return;
        }
        if (e.target.closest('[data-bpv-toggle]') ||
            (e.target.closest('.bpv__peek') && !this.classList.contains('is-open'))) {
          this.openDrawer();
        }
      });

      // Backdrop is a sibling of this element (outside it), bind it directly.
      const backdrop = (this.parentElement && this.parentElement.querySelector('.bpv__backdrop'))
        || document.querySelector('.bundle-preview-section .bpv__backdrop');
      if (backdrop) backdrop.addEventListener('click', () => this.closeDrawer());

      // Swipe up on the peek bar opens; swipe down on it closes.
      let startY = null;
      peek.addEventListener('touchstart', (e) => {
        startY = e.touches[0].clientY;
      }, { passive: true });
      peek.addEventListener('touchend', (e) => {
        if (startY === null) return;
        const delta = e.changedTouches[0].clientY - startY;
        startY = null;
        if (delta < -30) this.openDrawer();
        else if (delta > 30) this.closeDrawer();
      }, { passive: true });

      // Close on Escape.
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeDrawer();
      });
    }

    openDrawer() {
      if (this.classList.contains('is-open')) return;
      this.classList.add('is-open');
      const btn = this.querySelector('[data-bpv-toggle]');
      if (btn) btn.setAttribute('aria-expanded', 'true');
      // Lock page scroll behind the sheet (drawer exists below 1024px only).
      if (window.matchMedia('(max-width: 1023.98px)').matches) {
        document.body.style.overflow = 'hidden';
      }
    }

    closeDrawer() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      const btn = this.querySelector('[data-bpv-toggle]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    /* ---------- Tier config from the bundle app block ---------- */
    getTiers() {
      const root = document.getElementById('bundle-dashboard-root')
        || document.querySelector('[data-cib-bundle-cart-root]');
      if (!root) return { tiers: [], freeShipWeight: 0 };
      const d = root.dataset;

      // Variable-length tier list (data-tiers JSON); legacy tier1–4 fallback.
      let tiers = [];
      try {
        tiers = JSON.parse(d.tiers || '[]');
      } catch (e) {
        tiers = [];
      }
      if (!Array.isArray(tiers)) tiers = [];
      tiers = tiers
        .map((t) => ({ weight: Number(t.weight || 0), discount: Number(t.discount || 0) }))
        .filter((t) => t.weight > 0)
        .sort((a, b) => a.weight - b.weight);

      if (!tiers.length) {
        tiers = [
          { weight: Number(d.tier1Weight || 0), discount: Number(d.tier1Discount || 0) },
          { weight: Number(d.tier2Weight || 0), discount: Number(d.tier2Discount || 0) },
          { weight: Number(d.tier3Weight || 0), discount: Number(d.tier3Discount || 0) },
          { weight: Number(d.tier4Weight || 0), discount: Number(d.tier4Discount || 0) }
        ].filter((t) => t.weight > 0).sort((a, b) => a.weight - b.weight);
      }
      return { tiers, freeShipWeight: Number(d.freeShippingWeight || 0) };
    }

    /* ---------- Heat-sensitive detection (cold pack fee) ---------- */
    getHeatMap() {
      if (!this._heatMap) {
        this._heatMap = {};
        const el = this.querySelector('[data-bpv-heat-map]');
        if (el) {
          try {
            this._heatMap = JSON.parse(el.textContent) || {};
          } catch (e) {
            this._heatMap = {};
          }
        }
      }
      return this._heatMap;
    }

    isHeatSensitive(item) {
      const id = String(item.product_id || '');
      if (!id) return false;
      const map = this.getHeatMap();
      if (id in map) return map[id] === true;
      // Product added after page load: its grid card renders a
      // <heat-sensitive-badge> when the season.heat_senstive metafield is set.
      const card = document.querySelector('product-card-v3[data-product-id="' + id + '"], product-card-v2[data-product-id="' + id + '"], product-card[data-product-id="' + id + '"]');
      const hot = !!(card && card.querySelector('heat-sensitive-badge'));
      map[id] = hot; // cache
      return hot;
    }

    static tierFor(weight, tiers) {
      let match = { weight: 0, discount: 0 };
      for (let i = 0; i < tiers.length; i++) {
        if (weight >= tiers[i].weight) match = tiers[i];
      }
      return match;
    }

    /* ---------- Data ---------- */
    async fetchCart() {
      try {
        // Use the shared coalesced cart getter when available (one in-flight
        // /cart.js request + short cache shared across all cart consumers).
        if (window.cibCart) return await window.cibCart.get();
        const res = await fetch(window.Shopify ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
          headers: { Accept: 'application/json' }
        });
        if (!res.ok) return null;
        return await res.json();
      } catch (e) {
        return null;
      }
    }

    getBundleId() {
      const root = document.getElementById('bundle-dashboard-root')
        || document.querySelector('[data-cib-bundle-cart-root]');
      // undefined = no bundle config on the page (fall back to mode-only match)
      return root ? (root.dataset.bundleId || '') : undefined;
    }

    // Mirrors the app's getBundleItems() exactly: a line belongs to the bundle
    // only when it was tagged with _bundle_mode = 'weight_bundle' AND its
    // _bundle_id matches the current bundle. Items already in the cart (or from a
    // different bundle) are excluded.
    static isBundleItem(item, bundleId) {
      const p = item && item.properties;
      if (!p || p._bundle_mode !== 'weight_bundle') return false;
      if (bundleId === undefined) return true; // no config available on this page
      return String(p._bundle_id || '') === String(bundleId);
    }

    // Per-unit weight in lb: tagged weight → shipping grams → variant name ("1 lb").
    static itemWeightLb(item) {
      const tagged = Number((item.properties && item.properties._weight_lb) || 0);
      if (tagged > 0) return tagged;
      const grams = Number(item.grams || 0);
      if (grams > 0) return grams / 453.59237;
      const m = String(item.variant_title || item.title || '').toLowerCase()
        .match(/([\d.]+)\s*(lbs?|pounds?|oz|ounces?|kgs?|kilograms?|grams?|g)?\b/);
      if (!m) return 0;
      const num = parseFloat(m[1]);
      if (!isFinite(num) || num <= 0) return 0;
      const unit = m[2] || 'lb';
      if (unit.startsWith('oz') || unit.startsWith('ounce')) return num / 16;
      if (unit.startsWith('kg') || unit.startsWith('kilogram')) return num * 2.20462262;
      if (unit === 'g' || unit.startsWith('gram')) return num / 453.59237;
      return num;
    }

    async refresh(evt) {
      // Prefer the cart the theme hands us in the add-to-cart event, so the panel
      // updates immediately without a second /cart.js round-trip.
      const provided = evt && evt.detail && evt.detail.cart ? evt.detail.cart : null;
      const cart = provided || await this.fetchCart();
      const bundleId = this.getBundleId();
      const items = cart && Array.isArray(cart.items)
        ? cart.items.filter((item) => BundlePreview.isBundleItem(item, bundleId))
        : [];
      this.render(items);
    }

    /* ---------- Render ---------- */
    render(items) {
      const money = (c) => '$' + (Number(c || 0) / 100).toFixed(2);
      const { tiers, freeShipWeight } = this.getTiers();

      let weightLb = 0;
      let cartOriginal = 0; // line prices before automatic discounts (subs pricing already applied)
      let cartFinal = 0; // after line-level automatic discounts (e.g. the bundle discount)
      let subsSavings = 0; // real subscription savings, from selling plan allocations only

      items.forEach((item) => {
        const w = BundlePreview.itemWeightLb(item);
        weightLb += w * item.quantity;
        cartOriginal += Number(item.original_line_price != null ? item.original_line_price : item.line_price || 0);
        cartFinal += Number(item.final_line_price != null ? item.final_line_price : item.line_price || 0);

        // Subscription savings come ONLY from the selling plan allocation
        // (one-time price vs plan price). The original/final line price delta
        // is NOT subscription savings — that's where automatic discounts
        // (like the bundle bulk discount itself) show up.
        const spa = item.selling_plan_allocation;
        if (spa && spa.compare_at_price != null) {
          const perUnit = Number(spa.compare_at_price) - Number(spa.price || 0);
          if (perUnit > 0) subsSavings += perUnit * item.quantity;
        }
      });

      // "Products Total" = one-time prices before subscription pricing.
      const productsTotal = cartOriginal + subsSavings;

      const tier = BundlePreview.tierFor(weightLb, tiers);
      const bulkPct = tier.discount || 0;

      // Prefer the discount Shopify actually applied to the lines (the bundle
      // function is an automatic line discount, so it shows up in
      // final_line_price). Estimate from the tiers only when the cart doesn't
      // reflect it yet — and never subtract it twice.
      const appliedLineDiscount = Math.max(0, cartOriginal - cartFinal);
      const bulkDiscount = appliedLineDiscount > 0
        ? appliedLineDiscount
        : Math.round(cartFinal * (bulkPct / 100));
      const productsAfterDiscounts = appliedLineDiscount > 0
        ? cartFinal
        : Math.max(0, cartFinal - bulkDiscount);

      // Shipping: flat rate from theme settings (settings.flat_shipping_rate),
      // waived once the free-shipping weight is reached. Included in the
      // estimated total (in cents).
      const shipFree = freeShipWeight > 0 && weightLb >= freeShipWeight;
      const flatRate = parseFloat(this.dataset.flatShippingRate || '');
      const shippingCents = !shipFree && isFinite(flatRate) && flatRate > 0
        ? Math.round(flatRate * 100)
        : 0;

      // Cold pack protection (mirrors the cold-pack-fee-v2 checkout extension):
      // charged ONCE when any product is heat-sensitive AND the subtotal
      // (excluding the fee itself) is below the free-cold-pack threshold.
      const coldPackEnabled = String(this.dataset.coldPackEnabled) === 'true';
      const coldPackFee = parseFloat(this.dataset.coldPackFee || '');
      const coldPackThreshold = parseFloat(this.dataset.coldPackThreshold || '');
      const hasHeatSensitive = items.some((item) => this.isHeatSensitive(item));
      const underThreshold = isFinite(coldPackThreshold) && coldPackThreshold > 0
        ? productsAfterDiscounts < Math.round(coldPackThreshold * 100)
        : true;
      const coldPackCents = coldPackEnabled && hasHeatSensitive && underThreshold
        && isFinite(coldPackFee) && coldPackFee > 0
        ? Math.round(coldPackFee * 100)
        : 0;

      const estTotal = productsAfterDiscounts + shippingCents + coldPackCents;
      const filled = items.length > 0;

      this.classList.toggle('is-filled', filled);

      // Header + stats
      const countText = '(' + items.length + (items.length === 1 ? ' Item)' : ' Items)');
      this.$('[data-bpv-count]').textContent = countText;
      const peekCount = this.$('[data-bpv-count2]');
      if (peekCount) peekCount.textContent = countText;
      this.$('[data-bpv-weight]').textContent = (Math.round(weightLb * 100) / 100) + ' lb';
      this.$('[data-bpv-products]').textContent = String(items.length);
      this.$('[data-bpv-savings]').textContent = bulkPct > 0 ? bulkPct + '% off' : '0 off';
      this.$('[data-bpv-total]').textContent = filled ? money(estTotal) : '$0';

      // Empty / filled toggles
      this.$('[data-bpv-empty]').hidden = filled;
      this.$('[data-bpv-items]').hidden = !filled;
      this.$('[data-bpv-bd-empty]').hidden = filled;
      this.$('[data-bpv-bd-rows]').hidden = !filled;

      if (!filled) return;

      // Item rows (Figma 530-5760: thumb • title/meta • price + delete)
      this.list.innerHTML = items.map((item) => {
        const w = Math.round(BundlePreview.itemWeightLb(item) * 100) / 100;

        // Subscription badge shows the plan name ("Every 2 weeks").
        const spa = item.selling_plan_allocation;
        const planName = spa && spa.selling_plan && spa.selling_plan.name
          ? spa.selling_plan.name
          : (spa ? 'Subscribe' : '');
        const sub = planName
          ? '<span class="bpv__item-sub">' + BundlePreview.esc(planName) + '</span>' : '';

        const thumb = item.image
          ? '<img class="bpv__item-img" src="' + BundlePreview.esc(BundlePreview.sizedImage(item.image, 108)) + '" alt="" loading="lazy" width="54" height="54">'
          : '<span class="bpv__item-img bpv__item-img--ph" aria-hidden="true"></span>';

        return (
          '<li class="bpv__item">' +
            thumb +
            '<div class="bpv__item-main">' +
              '<p class="bpv__item-title">' + BundlePreview.esc(item.product_title || item.title) + '</p>' +
              '<div class="bpv__item-meta">' +
                '<span>' + (w || 0) + ' lb × ' + item.quantity + '</span>' + sub +
              '</div>' +
            '</div>' +
            '<div class="bpv__item-side">' +
              '<span class="bpv__item-price">' + money(item.final_line_price != null ? item.final_line_price : item.line_price) + '</span>' +
              '<button type="button" class="bpv__item-btn" data-bpv-remove="' + item.key + '" aria-label="Remove">' +
                '<svg viewBox="0 0 24 24" fill="none"><path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
              '</button>' +
            '</div>' +
          '</li>'
        );
      }).join('');

      // Breakdown
      this.$('[data-bpv-products-total]').textContent = money(productsTotal);
      const subsRow = this.$('[data-bpv-subs-row]');
      subsRow.hidden = subsSavings <= 0;
      this.$('[data-bpv-subs-savings]').textContent = '-' + money(subsSavings);
      const bulkRow = this.$('[data-bpv-bulk-row]');
      bulkRow.hidden = bulkDiscount <= 0;
      this.$('[data-bpv-bulk-label]').textContent = bulkPct > 0
        ? 'Bulk Discount (' + bulkPct + '%)'
        : 'Bulk Discount';
      this.$('[data-bpv-bulk]').textContent = '-' + money(bulkDiscount);
      const coldPackRow = this.$('[data-bpv-coldpack-row]');
      if (coldPackRow) {
        coldPackRow.hidden = coldPackCents <= 0;
        const cpLabel = this.$('[data-bpv-coldpack-label]');
        if (cpLabel && this.dataset.coldPackTitle) cpLabel.textContent = this.dataset.coldPackTitle;
        this.$('[data-bpv-coldpack]').textContent = money(coldPackCents);
      }
      const shipEl = this.$('[data-bpv-shipping]');
      if (shipFree) {
        shipEl.textContent = 'FREE';
      } else if (shippingCents > 0) {
        shipEl.textContent = money(shippingCents);
      } else {
        shipEl.textContent = 'Calculated at checkout';
      }
      shipEl.classList.toggle('bpv__green', shipFree);
      this.$('[data-bpv-est-total]').textContent = money(estTotal);
    }

    static esc(str) {
      return String(str || '').replace(/[&<>"]/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    }

    /* Shopify CDN image with an explicit pixel width (2x for retina thumbs). */
    static sizedImage(url, width) {
      const u = String(url || '');
      if (!u) return u;
      if (u.indexOf('width=') !== -1) return u;
      return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
    }

    /* ---------- Remove a bundle line ---------- */
    async removeLine(key) {
      try {
        await fetch((window.Shopify ? window.Shopify.routes.root : '/') + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        if (window.cibCart) window.cibCart.invalidate(); // cart changed
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch (e) {
        /* no-op */
      } finally {
        // Always re-render — clears the spinner whether the remove succeeded or failed.
        this.refresh();
      }
    }
  }

  customElements.define('bundle-preview', BundlePreview);
}
